import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { setAlert } from '../../../redux/actions';
import { getRandomArbitrary } from '../../utils/helper';
import useDidMountEffect from '../../utils/useDidMountEffect';
import PMedia from '../PMedia';
import PMediaExisting from '../PMediaExisting';
import { ReactSortable } from "react-sortablejs";
import Modal from 'react-bootstrap/Modal';
import Croppie from 'croppie';
import "croppie/croppie.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CloseButton from '../CloseButton';
import Switch from 'react-switch';

import './style.scss';

const maxFileSize = 1610612736; // 1.5GB

const SubCovers = ({ profileBkg }) => {
    const dispatch = useDispatch();

    const [filesizeErrors, setFilesizeErrors] = useState([]);
    const [error, setError] = useState('');
    const [files, setFiles] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [progressbar, setProgressbar] = useState([]);
    const [done, setDone] = useState({});
    const [tempUpload, setTempUpload] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [fileError, setFileError] = useState({});
    const [croppie, setCroppie] = useState("");
    const [show, setShow] = useState(false);
    const [hasSubcovers, setHasSubcovers] = useState(true);
    const [busy, setBusy] = useState(false);

    const {
        acceptedFiles,
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        onDropRejected,
        onDropAccepted,
        accept: 'image/jpeg, image/png, video/mp4, video/quicktime',
        maxFiles: 10,
        multiple: true,
        maxSize: maxFileSize,
    });

    const onDropRejected = useCallback((errors) => {
        console.error({ errors });
        let tmpErrors = [];
        errors.forEach(error => {
            if (error.errors.find(e => e.code === 'file-too-large')) {
                tmpErrors.push({
                    filename: error.file.name
                });
            }
        });
        if (tmpErrors) {
            setFilesizeErrors(tmpErrors);
            dispatch(setAlert({
                type: 'error',
                body: `Only ${maxFiles} files are accepted per post.`,
                show: true
            }));
        } else {
            setError('Only .jpg, .jpeg, .png, .mov, .mp4 files are accepted');
        }
    }, []);

    const onDropAccepted = useCallback((acceptedFiles) => {
        let rank = files.length > 0 ? files.length : 1;
        let existingFiles = [];

        setHasError(false);

        const acceptedFilesLn = acceptedFiles.length;
        const filesLn = files.length;
        const totalFiles = filesLn + acceptedFilesLn;

        acceptedFiles.forEach((file) => {
            const uuid = uuidv4();
            const cancelTokenSource = axios.CancelToken.source();
            const fileObj = {
                uuid: uuid,
                file: file,
                rank: rank++,
                image: URL.createObjectURL(file),
                loaded: 0,
                isLibrary: false,
                cancelTokenSource: cancelTokenSource,
                name: 'subcover.jpg'
            };

            existingFiles.push(fileObj);

            const mediaIds = new Set(files.map(fileState => fileState.uuid));
            if (!mediaIds.has(fileObj.uuid)) {
                files.push(fileObj);
                setFiles([...files]);
            }

            uploadFile(fileObj);
        });

        // existingFiles = [];
    }, [files]);


    const uploadFile = (file) => {
        const formData = new FormData();
        formData.append('media', file.file);
        formData.append('uuid', file.uuid);
        formData.append('rank', file.rank);
        formData.append('loaded', file.loaded);
        formData.append('source', 'sub_cover');
        axios.post(`/api/medias`, formData, {
            cancelToken: file.cancelTokenSource.token,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: progressEvent => {
                const loaded = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                setProgressbar({
                    ...progressbar,
                    [file.uuid]: loaded > 85 ? getRandomArbitrary(85, 99) : loaded
                });
            },
        }).then(response => {
            setDone(response.data);
            const tmp = [...tempUpload];
            tmp.push(response.data);
            setTempUpload(tmp);
        }).catch(error => {
            console.error(error);
            if (axios.isCancel(error)) {
                dispatch(setAlert({
                    type: 'warning',
                    body: error.message,
                    show: true
                }));
            } else {
                setFileError(file);
            }
        });
    };

    useDidMountEffect(() => {
        let mounted = true;

        if (fileError && Object.keys(fileError).length > 0) {
            const index = files.findIndex(item => item.uuid == fileError.uuid);
            files.splice(index, 1);
            dispatch(setAlert({
                type: 'error',
                body: `Failed to upload file ${fileError.file.name}`,
                show: true
            }));
        }

        return () => {
            mounted = false;
        };
    }, [fileError]);

    useDidMountEffect(() => {
        let mounted = true;
        if (done && Object.keys(done).length > 0) {
            const index = files.findIndex(item => item.uuid == done.uuid);
            const fileClones = [...files];
            if (index < 0) {
                fileClones.push({
                    ...done,
                    isLibrary: true,
                    loaded: 100,
                    uuid: done.uuid,
                    rank: done.rank,
                });
                setFiles([...fileClones]);
            } else {
                fileClones.splice(index, 1);
                fileClones.push(done);
                setFiles([...fileClones]);
            }

            updateSubCovers(done);
        }

        return () => {
            mounted = false;
        };
    }, [done]);

    const handleRemove = (file) => {
        if (file.cancelTokenSource) {
            file.cancelTokenSource.cancel('File upload cancelled');
        }

        // check files
        const uploadingIndex = files.findIndex(item => {
            if (item.uuid && file.uuid) {
                return item.uuid == file.uuid;
            }
        });

        if (uploadingIndex >= 0) {
            let cloned = [...files];
            cloned.splice(uploadingIndex, 1);
            setFiles([...cloned]);
        }

        if (file.media_id) {
            let cloned = [...files];
            const fileIndex = cloned.findIndex(item => item.media_id == file.media_id);
            if (fileIndex >= 0) {
                cloned.splice(fileIndex, 1);
                setFiles([...cloned]);
                updateSubCovers(null, [...cloned]);
            }
        }
    };

    const handleSort = (newState) => {
        const ranked = newState.map((state, index) => {
            return {
                ...state,
                rank: index
            };
        });

        setFiles(ranked);
    };

    const handleOnSort = () => {
        updateSubCovers();
    };

    const uploadChange = (event) => {
        const type = event.target.files[0].type;
        if (type.indexOf('image') < 0) {
            setError('Only .jpg, .jpeg, .png files are accepted');
            return;
        }
        handleCroppie(URL.createObjectURL(event.target.files[0]));
    };

    const handleCroppie = (image) => {
        var el = document.getElementById("image-helper");
        if (el) {
            var croppieInstance = new Croppie(el, {
                enableExif: true,
                boundary: {
                    height: 280
                },
                background: false,
                viewport: { width: 180, height: 240, type: 'square' },
                enableOrientation: true
            });

            croppieInstance.bind({
                url: image,
                orientation: 1
            });

            croppieInstance.result({
                type: 'jpg',
                size: 'viewport'
            });

            setCroppie(croppieInstance);
        }
    };

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setShow(true);
        setCroppie("");
    };

    const handleCropUpload = () => {
        croppie.result({
            type: 'blob',
            size: {
                width: 360,
                height: 480
            },
            format: 'jpg',
            quality: 1
        })
            .then(function (blob) {
                onDropAccepted([blob]);
            })
            .catch(error => {
                console.error({ error });
            });
    };

    const handleCroppieUpload = () => {
        handleCropUpload();
        handleClose();
    };

    const updateSubCovers = (newFile = null, arrFiles = []) => {
        let media = [];
        let tmp;

        if (files && Object.keys(files).length > 0) {
            tmp = [...files];
        }

        if (newFile) {
            tmp.push(newFile);
        }

        if (arrFiles.length > 0) {
            tmp = arrFiles;
        }

        let counter = 1;
        media = tmp.map((file, index) => {
            if (file.media_id) {
                return { media_id: file.media_id, rank: counter++ };
            }
        }).filter(file => file);

        axios.patch('/api/subcovers', {
            media
        })
            .then(response => {
                dispatch(setAlert({
                    show: true,
                    type: 'success',
                    body: 'Subcover updated'
                }));
            })
            .catch(error => {
                dispatch(setAlert({
                    show: true,
                    type: 'error',
                    body: 'Failed to update sub cover'
                }));
            });
    };

    const handleSubCovers = () => {
        setHasSubcovers(hasSubcovers => !hasSubcovers);
    };

    const getSubcovers = () => {
        setBusy(true);
        axios.get('/api/subcovers')
            .then(response => {
                // console.log('--------------->', response.data);
                setFiles(response.data);
                setBusy(false);
            })
            .catch(error => {
                setBusy(false);
            });
    };

    useEffect(() => {
        getSubcovers();
    }, []);


    if (!profileBkg) {
        return '';
    }

    return <div className='subcovers'>
        {
            hasSubcovers && <div className='subcover-label'>
                Sub Covers <button
                    type="button"
                    className='btn btn-subcover'
                    onClick={handleShow}
                >
                    <i className="fa-regular fa-image"></i>
                </button>
            </div>
        }
        <div className="d-flex">
            {/* <div>
                <div className="switch-wrap">
                    <label>
                        <Switch
                            onChange={handleSubCovers}
                            checked={hasSubcovers}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={35}
                            onColor="#0097fe"
                            disabled={busy}
                        />
                        <span className='subcover__label'>Sub Covers</span>
                    </label>
                </div>
            </div> */}


            {
                hasSubcovers && <div>
                    <ReactSortable
                        tag="div"
                        className="files__container"
                        list={files}
                        setList={(newState) => handleSort(newState)}
                        animation={200}
                        onChoose={() => { setIsDragging(true); }}
                        onUnchoose={() => { setIsDragging(false); }}
                        onSort={handleOnSort}
                        draggable=".files__file"
                        handle=".files__file-movable"
                        delayOnTouchOnly={true}
                        delay={2}
                        swap
                    >
                        {
                            files.map((file, index) => (
                                files.length === index + 1 && files.length > 0
                                    ? <React.Fragment key={file.media_id || file.uuid}>
                                        <div
                                            key={file.media_id || file.uuid}
                                            className={`files__file`}
                                        >
                                            <div className="files__file-file">
                                                {
                                                    file.media_id
                                                        ? <PMediaExisting
                                                            file={file}
                                                            index={index}
                                                            remove={handleRemove}
                                                            // updateThumbnailHandler={updateThumbnailHandler}
                                                            playable={false}
                                                        />
                                                        : <PMedia
                                                            file={file}
                                                            index={index}
                                                            remove={handleRemove}
                                                            progress={progressbar}
                                                        />
                                                }
                                            </div>
                                            {
                                                file.media_id && <div className='files__file-movable'>
                                                    <i className="fa-solid fa-grip-dots"></i>
                                                </div>
                                            }
                                            <div className="draggable d-block d-md-none d-lg-none d-xl-none">
                                                <div
                                                    onClick={(e) => handleRemove(file)}
                                                    style={{
                                                        textAlign: 'center',
                                                        padding: '4px',
                                                        background: '#fe7676',
                                                        color: '#fcfcfc',
                                                        fontSize: '11px',
                                                        borderRadius: '8px',
                                                        marginTop: 8,
                                                        marginBottom: 4
                                                    }}>
                                                    {!file.media_id ? 'CANCEL' : 'REMOVE'}
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                    : <div
                                        key={file.media_id || file.uuid}
                                        className={`files__file`}
                                    >
                                        <div className="files__file-file">
                                            {
                                                file.media_id
                                                    ? <PMediaExisting
                                                        file={file}
                                                        index={index}
                                                        remove={handleRemove}
                                                        // updateThumbnailHandler={updateThumbnailHandler}
                                                        playable={false}
                                                    />
                                                    : <PMedia
                                                        file={file}
                                                        index={index}
                                                        remove={handleRemove}
                                                        progress={progressbar}
                                                    />
                                            }
                                        </div>
                                        {
                                            file.media_id && <div className='files__file-movable'>
                                                <i className="fa-solid fa-grip-dots"></i>
                                            </div>
                                        }
                                        <div className="draggable d-block d-md-none d-lg-none d-xl-none">
                                            <div
                                                onClick={(e) => handleRemove(file)}
                                                style={{
                                                    textAlign: 'center',
                                                    padding: '4px',
                                                    background: '#fe7676',
                                                    color: '#fcfcfc',
                                                    fontSize: '11px',
                                                    borderRadius: '8px',
                                                    marginTop: 8,
                                                    marginBottom: 4
                                                }}>
                                                {!file.media_id ? 'CANCEL' : 'REMOVE'}
                                            </div>
                                        </div>
                                    </div>
                            ))
                        }
                    </ReactSortable>
                </div>
            }
        </div>

        <Modal className="prof-uploader text-center" show={show} onHide={handleClose} centered>
            <Modal.Header>
                <CloseButton
                    className="close"
                    style={{ position: 'absolute', right: '12px' }}
                    onClick={handleClose}
                />
                <Modal.Title><b>Position and size your photo</b></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div id="image-helper"></div>
                {croppie === "" && <div>
                    <span className="icon">
                        <FontAwesomeIcon icon={['fas', 'camera']} />
                    </span>
                    <span className="str">
                        Upload your sub cover here
                        <small className="um-max-filesize">( max: <span>10MB</span> )</small>
                    </span>
                    <div className="upload">
                        <input id="prof-upload" onChange={uploadChange} type="file" accept="image/jpeg, image/png" />
                        <button className="btn theme-btn theme-btn-bordered">Upload</button>
                    </div>

                    {error && <div className="mt-3 text-danger">{error}</div>}
                </div>}
                {croppie !== "" &&
                    <div className="prof-uploader-btns">
                        <div className="prof-uploader-btns-wrapper">
                            <button onClick={handleClose} className="btn theme-btn theme-btn-white theme-btn-bordered">Cancel</button>
                            <button onClick={handleCroppieUpload} className="btn theme-btn theme-btn-bordered">Save</button>
                        </div>
                    </div>
                }
            </Modal.Body>
        </Modal>
    </div>;
};

export default SubCovers;